import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, MapPin } from 'lucide-react';
import type { StoreProfileDetail } from '../../types';
import { TWIN_READINESS_STATE_LABELS } from '../../lib/twinReadiness';

interface ManageTwinHeaderProps {
  profile: StoreProfileDetail;
  storeIdParam: string;
}

const READINESS_STATE_STYLES: Record<StoreProfileDetail['twinReadiness']['state'], string> = {
  not_started: 'bg-gray-100 text-gray-600 border-gray-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  ready_to_publish: 'bg-amber-50 text-amber-700 border-amber-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** Compact admin header with consistent store-area and evidence counts. */
export function ManageTwinHeader({ profile }: ManageTwinHeaderProps) {
  const readiness = profile.twinReadiness;
  const stats = profile.evidenceMappingStats;

  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="px-4 py-3 border-b bg-white">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Badge variant="outline">{profile.retailer}</Badge>
          <Badge variant="outline">{profile.storeCode}</Badge>
        </div>
        <h1 className="text-lg font-semibold">{profile.storeName}</h1>
        <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3" aria-hidden />
          {profile.fullAddress}
        </p>
      </div>

      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">
        <div className="bg-white p-3">
          <dt className="text-xs text-gray-500">Twin readiness</dt>
          <dd className="mt-1">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{readiness.percent}%</span>
              <Badge variant="outline" className={READINESS_STATE_STYLES[readiness.state]}>
                {TWIN_READINESS_STATE_LABELS[readiness.state]}
              </Badge>
            </div>
            <Progress value={readiness.percent} className="h-1.5 mt-2" />
          </dd>
        </div>

        <div className="bg-white p-3">
          <dt className="text-xs text-gray-500">Store areas on floorplan</dt>
          <dd className="mt-1 text-lg font-semibold">{profile.storeAreasCount}</dd>
          <p className="text-[11px] text-gray-500">
            {profile.aisleCount} aisle{profile.aisleCount === 1 ? '' : 's'} · {profile.floorplanZones.length} zone{profile.floorplanZones.length === 1 ? '' : 's'}
          </p>
        </div>

        <div className="bg-white p-3">
          <dt className="text-xs text-gray-500">Photos & videos placed</dt>
          <dd className="mt-1 text-lg font-semibold">
            {stats.mapped}
            {stats.needsReview + stats.unplaced > 0 && (
              <span className="text-sm font-medium text-amber-600">
                {' '}· {stats.needsReview + stats.unplaced} remaining
              </span>
            )}
          </dd>
          <p className="text-[11px] text-gray-500">
            {stats.needsReview} suggested · {stats.unplaced} unplaced
          </p>
        </div>

        <div className="bg-white p-3">
          <dt className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" aria-hidden />
            Last verified
          </dt>
          <dd className="mt-1 text-lg font-semibold">{profile.lastSurveyDate ?? '—'}</dd>
        </div>
      </dl>
    </div>
  );
}
